import {useEffect,useRef} from 'react';

/** Decorative only: no pointer interception or React updates while moving. */
export function NewTaskDotBackground({ grid }: { grid?: { gap: number; offsetX: number; offsetY: number; radius: number } } = {}){
 const ref=useRef<HTMLCanvasElement>(null);
 useEffect(()=>{
  const canvas=ref.current,host=canvas?.parentElement;
  if(!canvas||!host)return;
  const context=canvas.getContext('2d');if(!context)return;
  const reduced=window.matchMedia('(prefers-reduced-motion: reduce)');
  let frame=0,width=0,height=0,x=0,y=0,started=0;
  let blocked: Array<{left:number;right:number;top:number;bottom:number}>=[];
  const clear=()=>{cancelAnimationFrame(frame);frame=0;context.clearRect(0,0,width,height);};
  const resize=()=>{const rect=host.getBoundingClientRect();width=rect.width;height=rect.height;const ratio=Math.min(window.devicePixelRatio||1,2);canvas.width=Math.round(width*ratio);canvas.height=Math.round(height*ratio);context.setTransform(ratio,0,0,ratio,0,0);};
  const draw=(now:number)=>{
   context.clearRect(0,0,width,height);
   const age=(now-started)/1600;if(age>=1||reduced.matches){frame=0;return;}
   const radius=28+age*54;
   const baseGap=grid?.gap??24,gap=baseGap*Math.max(1,Math.ceil(8/baseGap)),offsetX=grid?.offsetX??12,offsetY=grid?.offsetY??12;
   const firstX=Math.ceil((Math.max(0,x-180)-offsetX)/gap)*gap+offsetX;
   const firstY=Math.ceil((Math.max(0,y-180)-offsetY)/gap)*gap+offsetY;
   for(let dx=firstX;dx<Math.min(width,x+180);dx+=gap){
    for(let dy=firstY;dy<Math.min(height,y+180);dy+=gap){
     const distance=Math.hypot(dx-x,dy-y),pulse=Math.max(0,1-Math.abs(distance-radius)/36)*(1-age);
     if(pulse<=0||blocked.some(r=>dx>=r.left-3&&dx<=r.right+3&&dy>=r.top-3&&dy<=r.bottom+3))continue;
     context.beginPath();context.arc(dx,dy,Math.max(grid?.radius??1,.9)+.8*pulse,0,Math.PI*2);context.fillStyle=`rgba(139,109,132,${.32*pulse})`;context.fill();
    }
   }
   frame=requestAnimationFrame(draw);
  };
  const move=(event:PointerEvent)=>{
   const target=event.target instanceof Element?event.target:null;
   const covered=grid?!target?.classList.contains('react-flow__pane'):!!target?.closest('button,input,textarea,select,a,[role="dialog"],[data-ripple-block]');
   if(reduced.matches||event.pointerType==='touch'||covered){clear();return;}
   const now=performance.now();
   const rect=host.getBoundingClientRect();
   x=event.clientX-rect.left;y=event.clientY-rect.top;started=now;
   if(!frame)blocked=Array.from(host.querySelectorAll('.react-flow__node,[data-ripple-block]')).map(node=>{const r=node.getBoundingClientRect();return {left:r.left-rect.left,right:r.right-rect.left,top:r.top-rect.top,bottom:r.bottom-rect.top};});
   if(!frame)frame=requestAnimationFrame(draw);
  };
  const observer=new ResizeObserver(resize);observer.observe(host);resize();
  host.addEventListener('pointermove',move,{passive:true});host.addEventListener('pointerleave',clear);reduced.addEventListener('change',clear);
  return()=>{clear();observer.disconnect();host.removeEventListener('pointermove',move);host.removeEventListener('pointerleave',clear);reduced.removeEventListener('change',clear);};
 },[grid?.gap,grid?.offsetX,grid?.offsetY,grid?.radius]);
 return <canvas ref={ref} aria-hidden="true" data-testid="new-task-dot-background" className="pointer-events-none absolute inset-0 h-full w-full" style={{position:'absolute',inset:0,width:'100%',height:'100%',pointerEvents:'none',...(grid?{zIndex:0}:{backgroundImage:'radial-gradient(circle, #d9d5cf 1px, transparent 1px)',backgroundSize:'24px 24px',backgroundPosition:'0 0'})}}/>;
}
